import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { client, urlFor } from '../client';
import { PortableText } from '@portabletext/react';

const portableTextComponents = {
  types: {
    image: ({ value }) => {
      if (!value?.asset?._ref) {
        return null;
      }
      return (
        <img
          alt={value.alt || ' '}
          loading="lazy"
          src={urlFor(value).width(800).fit('max').auto('format').url()}
          className="my-6 rounded shadow-md"
        />
      );
    }
  },
  marks: {
    link: ({children, value}) => {
      const rel = !value.href.startsWith('/') ? 'noopener noreferrer' : undefined
      const target = !value.href.startsWith('/') ? '_blank' : undefined
      return (
        <a href={value.href} rel={rel} target={target} className="text-[#691e31] hover:underline">
          {children}
        </a>
      )
    },
  },
  block: {
    h2: ({children}) => <h2 className="text-2xl font-bold mt-8 mb-4 text-[#2a4865]">{children}</h2>,
    h3: ({children}) => <h3 className="text-xl font-bold mt-6 mb-3 text-[#2a4865]">{children}</h3>,
    h4: ({children}) => <h4 className="text-lg font-bold mt-4 mb-2 text-[#2a4865]">{children}</h4>,
    blockquote: ({children}) => <blockquote className="border-l-4 border-[#691e31] pl-4 py-2 my-6 italic text-gray-700">{children}</blockquote>,
    normal: ({children}) => <p className="text-gray-700 mb-6 leading-relaxed">{children}</p>,
  },
  list: {
    bullet: ({children}) => <ul className="list-disc pl-5 mb-6 space-y-2 text-gray-700">{children}</ul>,
    number: ({children}) => <ol className="list-decimal pl-5 mb-6 space-y-2 text-gray-700">{children}</ol>,
  },
  listItem: {
    bullet: ({children}) => <li>{children}</li>,
    number: ({children}) => <li>{children}</li>,
  },
};

const ProgramDetailPage = () => {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    const query = `*[_type == "program" && slug.current == $slug][0]{
      title,
      description,
      details,
      image,
      mediaGallery[]{
        _key,
        _type,
        caption,
        asset->{
          ...,
          metadata
        }
      }
    }`;

    setLoading(true);
    client.fetch(query, { slug })
      .then((data) => {
        setProgram(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching program data:', err);
        setError('Failed to load program content.');
        setLoading(false);
      });
  }, [slug]);

  const handlePrev = () => {
    setGalleryIndex((prevIndex) => 
      prevIndex === 0 ? program.mediaGallery.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setGalleryIndex((prevIndex) =>
      (prevIndex + 1) % program.mediaGallery.length
    );
  };
  
  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center h-screen">Error: {error}</div>;
  }

  if (!program) {
    return (
      <div className="text-center py-20">
        <h1 className="text-4xl font-bold text-[#2a4865] mb-4">Program Not Found</h1>
        <p className="text-gray-600 mb-8">Sorry, we couldn't find the program you're looking for.</p>
        <Link to="/" className="bg-[#691e31] text-white font-bold py-2 px-4 rounded hover:bg-[#541827] transition-colors">
          Return to Homepage
        </Link>
      </div>
    );
  }

  const hasMediaGallery = program.mediaGallery && program.mediaGallery.length > 0;

  return (
    <div className="bg-white">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {program.image && (
          <div className="w-full h-80 bg-gray-200 rounded-xl overflow-hidden mb-8 shadow-lg">
            <img 
              src={urlFor(program.image).url()} 
              alt={program.title}
              className="w-full h-full object-contain"
            />
          </div>
        )}
        
        <h1 className="text-4xl md:text-5xl font-extrabold text-[#2a4865] mb-3 relative">
          {program.title}
           <span className="block w-20 h-1 bg-yellow-500 mt-2"></span>
        </h1>
        
        {program.description && (
          <p className="text-lg text-gray-700 my-6 italic border-l-4 border-yellow-500 pl-4">
            {program.description}
          </p>
        )}
        
        {program.details && Array.isArray(program.details) && program.details.length > 0 && (
          <div className="prose prose-lg max-w-none text-gray-800 leading-relaxed">
            <PortableText
              value={program.details}
              components={portableTextComponents}
            />
          </div>
        )}

        {hasMediaGallery && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-[#2a4865] mb-6 relative after:content-[''] after:absolute after:w-16 after:h-1 after:bg-yellow-500 after:-bottom-3 after:left-0">
              Gallery
            </h2>
            <div className="relative w-full h-96 bg-gray-100 rounded-lg overflow-hidden shadow-lg">
              {/* Previous Button */}
              <button
                onClick={handlePrev}
                className="absolute top-1/2 left-2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-colors"
                aria-label="Previous image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              {/* Media Item */}
              {program.mediaGallery.map((item, index) => (
                <div 
                  key={item._key} 
                  className={`absolute inset-0 transition-opacity duration-500 ${index === galleryIndex ? 'opacity-100' : 'opacity-0'}`}
                >
                  {item._type === 'galleryImage' ? (
                    <img src={urlFor(item.asset).url()} alt={item.caption || program.title} className="w-full h-full object-contain" />
                  ) : (
                    <video src={item.asset.url} controls className="w-full h-full object-contain"></video>
                  )}
                  {item.caption && (
                    <div className="absolute bottom-0 left-0 w-full p-4 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white text-sm">{item.caption}</p>
                    </div>
                  )}
                </div>
              ))}

              {/* Next Button */}
              <button
                onClick={handleNext}
                className="absolute top-1/2 right-2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-colors"
                aria-label="Next image"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgramDetailPage; 